import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Camera, Mail, Instagram, Linkedin } from 'lucide-react';
import { LogoIcon } from './Logo';
import { getLangFromPath, localizePath } from '../i18n';

const serviceSlugs = [
  { slug: 'marcas', key: 'brands' },
  { slug: 'eventos', key: 'corporate' },
  { slug: 'fan-experience', key: 'fanExperience' },
  { slug: 'deportes', key: 'sports' },
  { slug: 'hospitality', key: 'hospitality' },
  { slug: 'fotografos-on-demand', key: 'photographersOnDemand' },
  { slug: 'productoras', key: 'productoras' },
];

export default function Footer() {
  const { t } = useTranslation();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);
  const to = (p) => localizePath(p, lang);

  return (
    <footer className="border-t border-white/5 bg-[#08080c] mt-24">
      <div className="container-mi py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <NavLink to={to('/')} className="inline-flex items-center gap-2">
              <LogoIcon size={32} />
              <span className="font-display text-xl font-semibold tracking-tight">
                moment<span className="bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent">IA</span>
              </span>
            </NavLink>
            <p className="mt-4 text-sm text-white/60 max-w-sm leading-relaxed">
              {t('footer.tagline')}
            </p>
            <div className="flex items-center gap-3 mt-6">
              {[
                { Icon: Instagram, href: 'https://www.instagram.com/moment.ia_/', label: 'Instagram' },
                { Icon: Linkedin, href: 'https://www.linkedin.com/company/momentia-fotografia/', label: 'LinkedIn' },
              ].map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:border-violet-400/50 hover:bg-violet-500/10 transition-colors"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-white/40 mb-4">{t('footer.product')}</div>
            <ul className="space-y-3 text-sm">
              <li><NavLink to={to('/vende-tus-fotos')} className="text-white/70 hover:text-white transition-colors">{t('nav.sellPhotos')}</NavLink></li>
              <li><NavLink to={to('/precios')} className="text-white/70 hover:text-white transition-colors">{t('nav.pricing')}</NavLink></li>
              <li><NavLink to={to('/request-demo')} className="text-white/70 hover:text-white transition-colors">{t('nav.requestDemo')}</NavLink></li>
              <li><a href="https://moment-ia.shop/face-search" className="text-white/70 hover:text-white transition-colors">{t('nav.findMyPhotos')}</a></li>
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-white/40 mb-4">{t('footer.servicesTitle')}</div>
            <ul className="space-y-3 text-sm">
              {serviceSlugs.map((s) => (
                <li key={s.slug}>
                  <NavLink to={to(`/servicios/${s.slug}`)} className="text-white/70 hover:text-white transition-colors">
                    {t(`services.${s.key}.title`)}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-white/40 mb-4">{t('footer.account')}</div>
            <ul className="space-y-3 text-sm">
              <li><a href="https://moment-ia.shop/login" className="text-white/70 hover:text-white transition-colors">{t('footer.login')}</a></li>
              <li><a href="https://moment-ia.shop/registro-fotografo" className="text-white/70 hover:text-white transition-colors">{t('footer.signupPhotographer')}</a></li>
              <li><NavLink to={to('/request-demo')} className="text-white/70 hover:text-white transition-colors">{t('footer.requestAccessCompany')}</NavLink></li>
            </ul>
            <div className="text-xs uppercase tracking-wider text-white/40 mt-6 mb-4">{t('footer.contact')}</div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2 text-white/70">
                <Mail className="w-4 h-4" /> <a href="mailto:equipo@moment-ia.shop" className="hover:text-white transition-colors">equipo@moment-ia.shop</a>
              </li>
              <li className="flex items-center gap-2 text-white/70">
                <Camera className="w-4 h-4" /> Buenos Aires, Argentina
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-white/40">
          <div>© {new Date().getFullYear()} momentIA. {t('footer.rights')}</div>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-white/70 transition-colors">{t('footer.terms')}</a>
            <a href="#" className="hover:text-white/70 transition-colors">{t('footer.privacy')}</a>
            <a href="#" className="hover:text-white/70 transition-colors">{t('footer.cookies')}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
